/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable consistent-return */
/* eslint-disable no-lonely-if */

import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Avatar, Input, Skeleton, Space, Tabs, Typography } from 'antd';
import RichTextEditor from 'components/RichTextEditor';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import ErrorResult from 'components/ErrorResult';
import apiRequests from 'utils/api';
import apiRoutes from 'config/apiRoute';
import asyncErrorHandler from 'utils/asyncErrorHandler';
import { generateColor, generateGravatarUrl, getUserInitials } from 'utils/string.utils';
import { getCountryByCode, getFlagUrl } from 'utils/countries';
import { socialMediaOptions } from 'config/media-options';
import { CheckOutlined, CopyOutlined, ExportOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import useSingleContact from '../edit/useSingleContact';
import MyTimelogs from './MyTimelogs';
import RequestOverdueReport from './RequestOverdueReport';
import TaskOverdueReport from './TaskOverdueReport';
import ClockReport from './ClockReport';
import Overview from './overview';
import { useProfileDateRange } from './useProfileDateRange';
import styles from './style.module.less';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface ProfileContactProps {
  uuid?: string;
  onEdit?: () => void;
  reduced?: boolean;
}

const defaultRequestParams = (userUuid: string) => {
  return {
    'page[size]': 129,
    'page[number]': 1,
    'filters[projects_requests.status][]': ['new', 'waiting_approval', 'approved', 'needs_attention', 'in_progress'],
    'filters[project.plan][]': ['starter', 'premium', 'enterprise', 'custom', 'hosting'],
    'filters[related_users.uuid][]': userUuid,
  };
};

const formatTimeInSavage = (startDate: string) => {
  if (!startDate) return undefined;
  const totalMonths = dayjs().diff(dayjs(startDate), 'month');

  if (totalMonths < 1) return 'Less than a month in Savage';

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years > 0 && months > 0) {
    return (
      <>
        <strong>{years}</strong> {years === 1 ? 'year' : 'years'} and <strong>{months}</strong> {months === 1 ? 'month' : 'months'} in Savage
      </>
    );
  }

  if (years > 0) {
    return (
      <>
        <strong>{years}</strong> {years === 1 ? 'year' : 'years'} in Savage
      </>
    );
  }

  return (
    <>
      <strong>{months}</strong> {months === 1 ? 'month' : 'months'} in Savage
    </>
  );
};

const formatContactResume = (contactResume: any) => {
  return {
    basics: {
      summary: contactResume?.basics?.summary || '',
      birth_date: contactResume?.basics?.birth_date || '',
      profiles: contactResume?.basics?.profiles || [],
    },
    interests: contactResume?.interests || [],
    work: contactResume?.work?.[0] || {
      name: '',
      position: '',
      position_description: '',
      startDate: '',
      endDate: '',
      summary: '',
      highlights: [],
    },
  };
};

const SOCIAL_URL_BUILDERS: Record<string, (username: string) => string> = {
  Instagram: (u) => `https://www.instagram.com/${u}`,
  Github:    (u) => `https://github.com/${u}`,
  Linkedin:  (u) => `https://www.linkedin.com/in/${u}`,
  Facebook:  (u) => `https://www.facebook.com/${u}`,
  X:         (u) => `https://x.com/${u}`,
  Twitch:    (u) => `https://www.twitch.tv/${u}`,
  Steam:     (u) => `https://steamcommunity.com/id/${u}`,
  Xbox:      (u) => `https://www.xbox.com/play/user/${u}`,
  VR:        (u) => `https://horizon.meta.com/profile/${u}`,
};

const COL1_NAMES = ['Interests', 'Tools', 'Skills', 'Music', 'Movies and TV Shows', 'Favorite quote'];
const COL2_NAMES = ['Games', 'Computer(s)', 'Phone(s)', 'Food', 'Color', 'Fun facts about me'];

let savedCarouselScroll = 0;

const TeamMemberCard = ({ member, compact, isActive }: { member: any; compact?: boolean, isActive?: boolean; }) => {
  const history = useHistory();
  const initials = getUserInitials(member as any);
  const avatarSize = compact ? 40 : 80;
  const cardClass = compact ? styles.memberCardCompact : styles.memberCard;

  const cardStyle = isActive
    ? { border: '2px solid #43d62b', borderRadius: '8px' }
    : {};

  return (
    <div className={cardClass} style={cardStyle} onClick={() => { history.push(`/people/${member.uuid}`); }}>
      <Avatar
        className="ant-avatar-full-string"
        shape="square"
        size={avatarSize}
        src={generateGravatarUrl(member.email, { size: avatarSize })}
      >
        <div style={{ width: '100% !important', background: generateColor(member.email ?? member.name) }}>{initials}</div>
      </Avatar>
      <Text className={styles.memberName}>{member.name}</Text>
      <Text ellipsis className={styles.memberJob}>{member.job_title || '-'}</Text>
    </div>
  );
};

const ProfileItem = ({ profile, networkIcon }: any) => {
  const [copied, setCopied] = useState(false);
  const content = profile.username;
  const isUrl = /^https?:\/\//i.test(content);
  const builder = SOCIAL_URL_BUILDERS[profile.network];
  const resolvedUrl = isUrl ? content : builder ? builder(content) : null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (resolvedUrl) {
      if (resolvedUrl.startsWith('mailto:')) {
        window.location.href = resolvedUrl;
      } else {
        window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const BrandIcon = networkIcon.icon;

  return (
    <div className={styles.profileItem} onClick={handleClick}>
      <div className={styles.originalContent}>
        <BrandIcon style={networkIcon.color ? { color: networkIcon.color } : {}} size={18} />
        <span>{profile.network}</span>
      </div>

      <div className={styles.hoverAction}>
        {resolvedUrl ? (
          <ExportOutlined />
        ) : copied ? (
          <CheckOutlined className={styles.checkIcon} />
        ) : (
          <CopyOutlined />
        )}
      </div>
    </div>
  );
};

const CopyableInfoItem = ({
  label,
  value,
  copyText,
}: {
  label: string;
  value: string | React.ReactNode | undefined;
  copyText?: string;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = copyText ?? (typeof value === 'string' ? value : '');
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.copyableItem} onClick={handleCopy}>
      <Text>{label}</Text>
      <div className={styles.copyableValue}>
        <Text ellipsis>{value ?? '-'}</Text>
        <span className={styles.copyIcon}>
          {copied ? <CheckOutlined className={styles.checkIcon} /> : <CopyOutlined />}
        </span>
      </div>
    </div>
  );
};

const ProfileContact: FC<ProfileContactProps> = ({ uuid, reduced = false, }) => {
  const user = useSelector((store: any) => store.auth.user);
  const { state } = useSingleContact(uuid);
  const contactData = state?.data?.data;
  const trackRef = useRef<HTMLDivElement>(null);
  const initials = getUserInitials(contactData as any);
  const { uuid: currentProfileUuid } = useParams<{ uuid: string }>();

  const [teamSearch, setTeamSearch] = useState('');

  const [requestActive, setRequestActive] = useState({
    loading: true,
    total: 0,
  });

  const [teamMembers, setTeamMembers] = useState<{ loading: boolean; data: any[] }>({
    loading: true,
    data: [],
  });

  const [activeTab, setActiveTab] = useState('personal');
  const hasInitializedTab = useRef(false);

  const [dateRange, setDateRange] = useProfileDateRange();

  useEffect(() => {
    if (!contactData) return;
    const allowed = user.is_super || user.contact.uuid === contactData.uuid;

    if (!allowed) {
      setActiveTab('personal');
      hasInitializedTab.current = true;
      return;
    }

    if (!hasInitializedTab.current) {
      setActiveTab('overview');
      hasInitializedTab.current = true;
    }
  }, [contactData, user.is_super, user.contact.uuid]);

  const fetchData = useCallback(async () => {
    try {
      const requestsRes = await apiRequests.get(apiRoutes.REQUESTS, defaultRequestParams(contactData.user.uuid));

      const responsibleRequests = requestsRes.data.data.filter((request: any) => {
        return request.related_users?.some((u: any) => u.pivot?.type === 'responsible');
      });

      setRequestActive({
        loading: false,
        total: responsibleRequests.length,
      });
    } catch (error) {
      asyncErrorHandler(error);
    }
  }, [contactData]);

  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await apiRequests.get(apiRoutes.CONTACTS, {
        'filters[type][]': ['regular_user'],
        'filters[billing_contacts.status][]': 'active',
      });
      setTeamMembers({ loading: false, data: res.data.data });
    } catch (error) {
      asyncErrorHandler(error);
      setTeamMembers((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    if (contactData && contactData.uuid) {
      fetchData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactData]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  useEffect(() => {
    if (!teamMembers.loading && trackRef.current) {
      trackRef.current.scrollLeft = savedCarouselScroll;
    }
  }, [teamMembers.loading]);

  const handleCarouselScroll = () => {
    if (trackRef.current) savedCarouselScroll = trackRef.current.scrollLeft;
  };

  const scrollCarousel = (dir: 'prev' | 'next') => {
    if (!trackRef.current) return;

    const track = trackRef.current;
    const itemWidth = 160 + 12;
    const scrollStep = itemWidth * 3;
    const maxScroll = track.scrollWidth - track.clientWidth;

    if (dir === 'next') {
      if (track.scrollLeft >= maxScroll - 5) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: scrollStep, behavior: 'smooth' });
      }
    } else {
      if (track.scrollLeft <= 5) {
        track.scrollTo({ left: maxScroll, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: -scrollStep, behavior: 'smooth' });
      }
    }
  };

  if (state.loading && !state.data) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active />
      </div>
    );
  }

  if (!state.data) {
    return <ErrorResult error={state.fetchError} />;
  }

  const resume = formatContactResume(contactData?.user?.resume);
  const isAllowedToEdit = user.is_super || user.contact.uuid === contactData.uuid;
  const canNavigateTabs = user.is_super || user.contact.uuid === contactData.uuid;
  const timeInSavage = formatTimeInSavage(resume.work.startDate);
  const carouselMembers = teamMembers.data;

  const pinnedMember = carouselMembers.find((m: any) => m.uuid === user.contact?.uuid);

  const filteredCarouselMembers = carouselMembers
  .filter((m: any) => {
    const matchesSearch = m.name?.toLowerCase().includes(teamSearch.toLowerCase());
    return matchesSearch && m.uuid !== user.contact?.uuid;
  })
  .sort((a: any, b: any) => {
    const nameA = a.name ?? '';
    const nameB = b.name ?? '';
    return nameA.localeCompare(nameB);
  });

  const hasActiveRequests = !requestActive.loading && requestActive.total > 0;
  const shouldRenderHeaderFooter = timeInSavage || hasActiveRequests;

  const interestsData = resume.interests.filter((i: any) => i.keywords?.length > 0);
  const column1 = interestsData.filter((i: any) => COL1_NAMES.includes(i.name));
  const column2 = interestsData.filter((i: any) => COL2_NAMES.includes(i.name));

  const renderInterestBlock = (interest: any, extraClass = '') => (
    <div key={interest.name} className={`${styles.interestBlock} ${extraClass}`}>
      <Text className={styles.interestTitle}>{interest.name}</Text>
      <div className={styles.interestList}>
        {interest.keywords.map((keyword: string, ki: number) => (
          <div key={ki} className={styles.interestItem}>{keyword}</div>
        ))}
      </div>
    </div>
  );

  const profileHeader = (
    <div className={`${styles.header} ${reduced ? styles.reducedHeader : ''}`} style={reduced ? { marginBottom: 8 } : {}}>
      <Avatar
          key={contactData.uuid}
          className={`ant-avatar-full-string ${styles.avatar}`}
          shape="square"
          size={80}
          src={generateGravatarUrl(contactData.email, { size: 80 })}
        >
          <div className={styles.avatarInitials} style={{ background: generateColor(contactData.email ?? contactData.name) }}>{initials}</div>
        </Avatar>
        <div className={styles.presentation}>
          <div className={`${styles.name} ${reduced ? styles.reducedNameLayout : ''}`}>
            <Title level={4}>{contactData.name}</Title>
            {resume.work.position !== '' && (
              <Title level={4} className={styles.position}>
                {!reduced && '•' } {resume.work.position}
              </Title>
            )}
          </div>

          {resume?.work?.position_description?.trim() && (
            <Text className={styles.position}>
              {resume.work.position_description}
            </Text>
          )}

          {shouldRenderHeaderFooter &&
            <div className={styles.headerFooter}>
              {timeInSavage && (
                <div>
                  <span>{timeInSavage}</span>
                </div>
              )}

              {hasActiveRequests && (
                <div>
                  <span>
                    <strong>{requestActive.total}</strong>
                  </span>
                  <span> active request{requestActive.total > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          }
        </div>

        {
          isAllowedToEdit &&
          <div className={reduced ? styles.reducedEditButton : styles.editButton}>
            <Link to={`/people/${contactData.uuid}/edit?from=profile`}>
              Edit
            </Link>
          </div>
        }
      </div>
  );

  const personalInfoContent = (
    <div className={styles.personalInfoCards}>
      {reduced && profileHeader}

      <div className={styles.cardBody}>
        <Title level={4}>About me</Title>

        <p className={resume.basics.summary ? styles.summary : styles.noSummary}>
          <RichTextEditor
            key={contactData.uuid}
            initialValue={resume.basics.summary || 'This user has not yet added their bio.'}
            className="quill-unthemed"
            placeholder="Write something about yourself..."
            readonly
          />
        </p>

        <div className={reduced ? styles.resumeInterestsReduced : styles.resumeInterestsCustom}>
          {/* COL 1 */}
          <div className={styles.interestColumn}>
            {column1.map((i: any) => renderInterestBlock(i))}
          </div>

          {/* COL2 2 */}
          <div className={styles.interestColumn}>
            {column2.filter((i: any) => i.name === 'Games').map((i: any) => renderInterestBlock(i))}

            <div className={styles.sideBySideRow}>
              {column2.filter((i: any) => i.name === 'Computer(s)').map((i: any) => renderInterestBlock(i, styles.flex1))}
              {column2.filter((i: any) => i.name === 'Phone(s)').map((i: any) => renderInterestBlock(i, styles.flex1))}
            </div>

            {column2
              .filter((i: any) => !['Games', 'Computer(s)', 'Phone(s)'].includes(i.name))
              .map((i: any) => renderInterestBlock(i))
            }
          </div>
        </div>
      </div>

      <div className={styles.cardBodySecondary}>
        <Title level={4}>Personal Info</Title>

        <div className={styles.personalGrid}>
          <div className={styles.infoCol1}>
            <CopyableInfoItem label="Name" value={contactData.name} />
            <CopyableInfoItem
              label="Birthday"
              value={resume.basics.birth_date ? dayjs(resume.basics.birth_date).format('MMMM, D') : undefined}
            />
            <CopyableInfoItem label="Phone" value={contactData.phone} />
            {(() => {
              const countryData = getCountryByCode(contactData.country);
              const locationLine1 = [contactData.city, contactData.state].filter(Boolean).join(', ');
              const locationLine2 = countryData ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <img
                    src={getFlagUrl(countryData.code)}
                    alt={countryData.code}
                    width={16}
                    style={{ marginRight: 4, verticalAlign: 'middle' }}
                  />
                  {countryData.name}
                </span>
              ) : (contactData.country || undefined);
              const locationCopyText = [
                locationLine1,
                countryData ? countryData.name : contactData.country,
              ].filter(Boolean).join('\n');
              const locationDisplay = locationLine1 || locationLine2 ? (
                <>
                  {locationLine1 && <span>{locationLine1}</span>}
                  {locationLine1 && locationLine2 && <br />}
                  {locationLine2}
                </>
              ) : undefined;
              return (
                <CopyableInfoItem
                  label="Location"
                  value={locationDisplay}
                  copyText={locationCopyText || undefined}
                />
              );
            })()}
            <CopyableInfoItem label="Phone 2" value={contactData.phone_2} />

            <CopyableInfoItem label="Email" value={contactData.email} />
          </div>
          <div className={styles.infoCol2}>
            <Text>Social media links</Text>
            <div className={styles.profileList}>
              {resume.basics.profiles.map((profile: any) => {
                const networkIcon = socialMediaOptions.find((socialOpt) => socialOpt.value === profile.network);

                if (!networkIcon) return null;

                return <ProfileItem key={profile.network} profile={profile} networkIcon={networkIcon} />;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Space className={`w-full ${reduced? styles.noSpaced : styles.spaceWrapper}`} direction="vertical">
      {!reduced && !teamMembers.loading && carouselMembers.length > 0 && (
        <div className={styles.carouselSection}>
          <div className={styles.carouselHeader}>
            <div className={styles.carouselTitleWrapper}>
              <Title className={styles.carouselTitle}>Team members</Title>
              <Input
                placeholder="Search..."
                allowClear
                value={teamSearch}
                onChange={(e) => setTeamSearch(e.target.value)}
                className={styles.teamSearch}
              />
            </div>

            <div className={styles.carouselButtons}>
              <button type="button" className={styles.carouselBtn} onClick={() => scrollCarousel('prev')}>
                <LeftOutlined />
              </button>
              <button type="button" className={styles.carouselBtn} onClick={() => scrollCarousel('next')}>
                <RightOutlined />
              </button>
            </div>
          </div>
          <div className={styles.carouselWrapper}>
            {pinnedMember && (
              <div className={styles.pinnedMember}>
                <TeamMemberCard
                  member={pinnedMember}
                  compact
                  isActive={pinnedMember.uuid === currentProfileUuid}
                />
              </div>
            )}
            <div ref={trackRef} className={styles.carouselTrack} onScroll={handleCarouselScroll}>
              {filteredCarouselMembers.map((member: any) => (
                <TeamMemberCard key={member.uuid} member={member} compact isActive={member.uuid === currentProfileUuid} />
              ))}
            </div>

          </div>
        </div>
      )}

      <div className={reduced ? styles.reducedContainer : styles.container}>
        {reduced ? (
          personalInfoContent
        ) : (
          <>
            {profileHeader}
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              className={`${styles.profileTabs} ${canNavigateTabs ? '' : styles.singleTab}`}
              style={{ marginTop: 8 }}
              destroyInactiveTabPane
            >
              <TabPane tab="Personal info" key="personal">
                {personalInfoContent}
              </TabPane>
              {canNavigateTabs && (
                <TabPane tab="Overview" key="overview">
                  <Overview
                    userUuid={contactData?.user?.uuid}
                    period={dateRange}
                    onPeriodChange={(range) => range && setDateRange(range)}
                  />
                </TabPane>
              )}
              {canNavigateTabs && (
                <TabPane tab="Request overdue report" key="request-overdue">
                  {contactData?.user?.uuid && (
                    <RequestOverdueReport
                      userUuid={contactData.user.uuid}
                      dateRange={dateRange}
                      onDateRangeChange={setDateRange}
                    />
                  )}
                </TabPane>
              )}
              {canNavigateTabs && (
                <TabPane tab="Task overdue report" key="task-overdue">
                  {contactData?.user?.uuid && (
                    <TaskOverdueReport
                      userUuid={contactData.user.uuid}
                      dateRange={dateRange}
                      onDateRangeChange={setDateRange}
                    />
                  )}
                </TabPane>
              )}
              {canNavigateTabs && (
                <TabPane tab="Timelogs" key="timelogs">
                  {contactData?.user?.uuid && (
                    <MyTimelogs userUuid={contactData.user.uuid} dateRange={dateRange} onDateRangeChange={setDateRange} />
                  )}
                </TabPane>
              )}
              {canNavigateTabs && (
                <TabPane tab="Clock in/out" key="clock-in">
                  {contactData?.user?.uuid && (
                    <ClockReport userUuid={contactData.user.uuid} dateRange={dateRange} onDateRangeChange={setDateRange} />
                  )}
                </TabPane>
              )}
            </Tabs>
          </>
        )}
      </div>
    </Space>
  );
};

export default ProfileContact;
